import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import PhotoUpload from "../components/PhotoUpload";
import GpsField from "../components/GpsField";
import HealthStatusSelect from "../components/HealthStatusSelect";
import { AGE_GROUPS, LEVELS_OF_PREVENTION, ageGroupFromAge } from "../lib/constants";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft } from "lucide-react";

const EMPTY_MEMBER = {
  name: "", age: 0, gender: "male", education: "", occupation: "", health_status: "", is_patient: false,
};

export default function HouseholdForm() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = !!editId;
  const [saving, setSaving] = useState(false);
  const [villages, setVillages] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [form, setForm] = useState({
    village: "",
    head_of_family: "",
    address: "",
    phone: "",
    visit_date: new Date().toISOString().slice(0, 10),
    family_members: [{ ...EMPTY_MEMBER }],
    water_source: "",
    toilet_facility: "",
    waste_disposal: "",
    ventilation: "",
    overall_hygiene: "fair",
    health_issues: [],
    health_issues_text: "",
    level_of_prevention: "",
    care_provided: "",
    education_provided: "",
    patient_photo_paths: [],
    remarks: "",
    latitude: null,
    longitude: null,
  });
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/villages").then((r) => setVillages(r.data.map((v) => v.name))).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/households/${editId}`)
      .then((r) => {
        const h = r.data;
        setForm({
          village: h.village || "",
          head_of_family: h.head_of_family || "",
          address: h.address || "",
          phone: h.phone || "",
          visit_date: h.visit_date || new Date().toISOString().slice(0, 10),
          family_members: h.family_members?.length ? h.family_members : [{ ...EMPTY_MEMBER }],
          water_source: h.water_source || "",
          toilet_facility: h.toilet_facility || "",
          waste_disposal: h.waste_disposal || "",
          ventilation: h.ventilation || "",
          overall_hygiene: h.overall_hygiene || "fair",
          health_issues: h.health_issues || [],
          health_issues_text: (h.health_issues || []).join(", "),
          level_of_prevention: h.level_of_prevention || "",
          care_provided: h.care_provided || "",
          education_provided: h.education_provided || "",
          patient_photo_paths: h.patient_photo_paths?.length ? h.patient_photo_paths : (h.patient_photo_path ? [h.patient_photo_path] : []),
          remarks: h.remarks || "",
          latitude: h.latitude ?? null,
          longitude: h.longitude ?? null,
        });
      })
      .catch((e) => toast.error(formatErr(e)))
      .finally(() => setLoading(false));
  }, [isEdit, editId]);

  const updateMember = (i, k, v) => {
    setForm((f) => {
      const fm = [...f.family_members];
      fm[i] = { ...fm[i], [k]: v };
      // auto-derive age_group when age is set if user hasn't picked one
      if (k === "age" && !fm[i].age_group) {
        fm[i].age_group = ageGroupFromAge(v);
      }
      return { ...f, family_members: fm };
    });
  };

  const addMember = () => update("family_members", [...form.family_members, { ...EMPTY_MEMBER }]);
  const removeMember = (i) => update("family_members", form.family_members.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        health_issues: form.health_issues_text
          .split(",").map((s) => s.trim()).filter(Boolean),
        patient_photo_path: form.patient_photo_paths[0] || null,
        patient_photo_paths: form.patient_photo_paths,
        family_members: form.family_members.map((m) => ({ ...m, age: parseInt(m.age, 10) || 0 })),
      };
      delete payload.health_issues_text;
      if (isEdit) {
        await api.put(`/households/${editId}`, payload);
        toast.success("Household updated");
        navigate(`/households/${editId}`);
      } else {
        await api.post("/households", payload);
        toast.success("Household visit saved");
        navigate("/households");
      }
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-up max-w-4xl mx-auto">
      <button
        type="button"
        onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/households"))}
        className="inline-flex items-center gap-2 h-11 px-4 rounded-md border-2 border-brand-primary bg-white text-brand-primary font-bold text-sm shadow-sm hover:bg-brand-primary hover:text-white transition-colors"
        data-testid="back-btn"
        aria-label="Back to previous screen"
      >
        <ArrowLeft className="h-5 w-5" strokeWidth={3} /> Back
      </button>

      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-brand-muted font-semibold">{isEdit ? "Edit visit" : "New Visit"}</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-1">Household Survey</h1>
      </div>

      {loading ? <div className="text-brand-muted">Loading…</div> : (
      <form onSubmit={submit} className="space-y-5" data-testid="household-form">
        <Section title="Basic Details">
          <Grid>
            <Field label="Village *">
              {villages.length > 0 ? (
                <select required value={form.village} onChange={(e) => update("village", e.target.value)} className="w-full h-12 px-3 rounded-md bg-brand-surfaceAlt border border-brand-border focus:bg-white focus:ring-2 focus:ring-brand-primary outline-none" data-testid="hh-village">
                  <option value="">Choose village…</option>
                  {villages.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              ) : (
                <Input required value={form.village} onChange={(e) => update("village", e.target.value)} data-testid="hh-village" />
              )}
            </Field>
            <Field label="Head of Family *">
              <Input required value={form.head_of_family} onChange={(e) => update("head_of_family", e.target.value)} data-testid="hh-head" />
            </Field>
            <Field label="Visit Date *">
              <Input type="date" required value={form.visit_date} onChange={(e) => update("visit_date", e.target.value)} data-testid="hh-date" />
            </Field>
            <Field label="Phone">
              <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="hh-phone" />
            </Field>
            <Field label="Address" className="md:col-span-2">
              <Input value={form.address} onChange={(e) => update("address", e.target.value)} data-testid="hh-address" />
            </Field>
          </Grid>
        </Section>

        <Section
          title="Family Members"
          action={
            <button type="button" onClick={addMember} className="inline-flex items-center gap-1 h-9 px-3 rounded-md border border-brand-border bg-white hover:bg-brand-surfaceAlt text-sm font-medium" data-testid="add-member-btn">
              <Plus className="h-4 w-4" /> Add member
            </button>
          }
        >
          <div className="space-y-3">
            {form.family_members.map((m, i) => (
              <div key={i} className="rounded-md border border-brand-border bg-brand-surfaceAlt p-4" data-testid={`member-row-${i}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs uppercase tracking-wider text-brand-muted font-semibold">Member #{i + 1}</div>
                  {form.family_members.length > 1 && (
                    <button type="button" onClick={() => removeMember(i)} className="text-brand-danger hover:text-red-700" data-testid={`remove-member-${i}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <Grid>
                  <Field label="Name *">
                    <Input required value={m.name} onChange={(e) => updateMember(i, "name", e.target.value)} />
                  </Field>
                  <Field label="Age *">
                    <Input type="number" required min={0} value={m.age} onChange={(e) => updateMember(i, "age", e.target.value)} />
                  </Field>
                  <Field label="Age Group">
                    <Select value={m.age_group || ""} onChange={(e) => updateMember(i, "age_group", e.target.value)}>
                      <option value="">Auto-detect</option>
                      {AGE_GROUPS.map((g) => <option key={g} value={g}>{g}</option>)}
                    </Select>
                  </Field>
                  <Field label="Gender">
                    <Select value={m.gender} onChange={(e) => updateMember(i, "gender", e.target.value)}>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </Select>
                  </Field>
                  <Field label="Education">
                    <Input value={m.education} onChange={(e) => updateMember(i, "education", e.target.value)} />
                  </Field>
                  <Field label="Occupation">
                    <Input value={m.occupation} onChange={(e) => updateMember(i, "occupation", e.target.value)} />
                  </Field>
                  <Field label="Health Status">
                    <HealthStatusSelect value={m.health_status} onChange={(v) => updateMember(i, "health_status", v)} testId={`hh-member-hs-${i}`} />
                  </Field>
                  <label className="flex items-center gap-2 text-sm md:col-span-2 mt-1">
                    <input type="checkbox" checked={m.is_patient} onChange={(e) => updateMember(i, "is_patient", e.target.checked)} />
                    <span>Mark as patient (needs care)</span>
                  </label>
                </Grid>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Home Hygiene Observation">
          <Grid>
            <Field label="Water Source"><Input value={form.water_source} onChange={(e) => update("water_source", e.target.value)} placeholder="Tap / Well / Pond" /></Field>
            <Field label="Toilet Facility"><Input value={form.toilet_facility} onChange={(e) => update("toilet_facility", e.target.value)} placeholder="Pucca / Open" /></Field>
            <Field label="Waste Disposal"><Input value={form.waste_disposal} onChange={(e) => update("waste_disposal", e.target.value)} /></Field>
            <Field label="Ventilation"><Input value={form.ventilation} onChange={(e) => update("ventilation", e.target.value)} /></Field>
            <Field label="Overall Hygiene">
              <Select value={form.overall_hygiene} onChange={(e) => update("overall_hygiene", e.target.value)} data-testid="hh-hygiene">
                <option value="poor">Poor</option>
                <option value="fair">Fair</option>
                <option value="good">Good</option>
                <option value="excellent">Excellent</option>
              </Select>
            </Field>
          </Grid>
        </Section>

        <Section title="Health Assessment & Care Provided">
          <div className="space-y-3">
            <Field label="Health Issues (comma separated)">
              <Input value={form.health_issues_text} onChange={(e) => update("health_issues_text", e.target.value)} placeholder="Fever, Cough, Diabetes" data-testid="hh-issues" />
            </Field>
            <Field label="Level of Prevention">
              <select value={form.level_of_prevention} onChange={(e) => update("level_of_prevention", e.target.value)} className="w-full h-12 px-3 rounded-md bg-brand-surfaceAlt border border-brand-border focus:bg-white focus:ring-2 focus:ring-brand-primary outline-none" data-testid="hh-prevention">
                <option value="">Select level…</option>
                {LEVELS_OF_PREVENTION.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </Field>
            <Field label="Care Provided">
              <Textarea value={form.care_provided} onChange={(e) => update("care_provided", e.target.value)} placeholder="First-aid, BP check, etc." />
            </Field>
            <Field label="Education Provided">
              <Textarea value={form.education_provided} onChange={(e) => update("education_provided", e.target.value)} placeholder="Hand hygiene, balanced diet, vaccination awareness…" />
            </Field>
            <Field label="Patient / Household Photos (max 3)">
              <PhotoUpload
                value={form.patient_photo_paths}
                onChange={(v) => update("patient_photo_paths", v)}
                multiple={true}
                maxCount={3}
                testId="hh-photo"
              />
            </Field>
            <Field label="Location (GPS)">
              <GpsField
                latitude={form.latitude}
                longitude={form.longitude}
                onChange={(lat, lng) => setForm((f) => ({ ...f, latitude: lat, longitude: lng }))}
              />
            </Field>
            <Field label="Remarks">
              <Textarea value={form.remarks} onChange={(e) => update("remarks", e.target.value)} />
            </Field>
          </div>
        </Section>

        <div className="sticky bottom-20 md:bottom-4 z-10">
          <button
            type="submit"
            disabled={saving}
            className="w-full md:w-auto h-12 px-8 rounded-md bg-brand-primary hover:bg-brand-primaryHover text-white font-medium transition-colors disabled:opacity-60"
            data-testid="hh-submit"
          >
            {saving ? "Saving…" : isEdit ? "Update Visit" : "Save Visit"}
          </button>
        </div>
      </form>
      )}
    </div>
  );
}

function Section({ title, children, action }) {
  return (
    <div className="bg-white border border-brand-border rounded-lg p-5 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-lg font-semibold">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}

function Grid({ children }) { return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>; }
function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-xs font-semibold uppercase tracking-wider text-brand-muted">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
function Input(props) {
  return <input {...props} className="w-full h-12 px-3 rounded-md bg-brand-surfaceAlt border border-brand-border focus:bg-white focus:ring-2 focus:ring-brand-primary focus:ring-offset-1 outline-none transition-all" />;
}
function Select({ children, ...props }) {
  return (
    <select {...props} className="w-full h-12 px-3 rounded-md bg-brand-surfaceAlt border border-brand-border focus:bg-white focus:ring-2 focus:ring-brand-primary outline-none">{children}</select>
  );
}
function Textarea(props) {
  return <textarea {...props} rows={3} className="w-full px-3 py-2 rounded-md bg-brand-surfaceAlt border border-brand-border focus:bg-white focus:ring-2 focus:ring-brand-primary outline-none resize-y" />;
}
